/**
 * Base CLI Parser
 *
 * Abstract base class providing common functionality for all CLI parsers.
 * Handles:
 * - Flag/option parsing
 * - Environment variable extraction
 * - Header parsing
 * - Config building
 */

import type { McpServerConfig, ParsedMcpServer } from '../../types';
import type {
  CliParseOptions,
  CliParseResult,
  CliTool,
  ICliParser,
  ParsedCliFlags,
} from '../types';

/**
 * Abstract base class for CLI parsers
 */
export abstract class BaseCliParser implements ICliParser {
  abstract readonly tool: CliTool;

  abstract readonly displayName: string;

  abstract readonly prefixes: string[];

  /**
   * Check if this parser can handle the given tokens
   */
  canHandle(tokens: string[]): boolean {
    const command = tokens.join(' ').toLowerCase();
    return this.prefixes.some((prefix) => command.startsWith(prefix.toLowerCase()));
  }

  /**
   * Parse the CLI command - implemented by subclasses
   */
  abstract parse(options: CliParseOptions): CliParseResult;

  /**
   * Parse a header string in format "Header-Name: Header-Value"
   */
  protected static parseHeader(header: string): { key: string; value: string } | null {
    const colonIndex = header.indexOf(':');
    if (colonIndex <= 0) {
      return null;
    }
    return {
      key: header.slice(0, colonIndex).trim(),
      value: header.slice(colonIndex + 1).trim(),
    };
  }

  /**
   * Parse an environment variable string in format "KEY=VALUE"
   */
  protected static parseEnvVar(envVar: string): { key: string; value: string } | null {
    const equalsIndex = envVar.indexOf('=');
    if (equalsIndex <= 0) {
      return null;
    }
    return {
      key: envVar.slice(0, equalsIndex),
      value: envVar.slice(equalsIndex + 1),
    };
  }

  /**
   * Extract the server name from args or URL
   * Used as fallback when name isn't explicitly provided
   */
  protected static extractNameFromArgs(args: string[]): string | undefined {
    // Look for package names in args
    return args.reduce<string | undefined>((acc, arg) => {
      if (acc) return acc;
      // npm package pattern: @scope/package or package
      const packageMatch = arg.match(/@[\w-]+\/[\w-]+|^[\w-]+$/);
      if (packageMatch) {
        // Extract just the package name without scope
        const name = packageMatch[0].split('/').pop();
        if (name && !name.startsWith('-')) {
          return name.replace(/^@/, '');
        }
      }
      return undefined;
    }, undefined);
  }

  /**
   * Extract server name from URL
   */
  protected static extractNameFromUrl(url: string): string | undefined {
    try {
      const urlObj = new URL(url);
      // Try to get name from hostname subdomain
      const hostParts = urlObj.hostname.split('.');
      if (hostParts.length > 2) {
        // e.g., mcp.example.com -> example
        return hostParts[hostParts.length - 2];
      }
      // e.g., example.com -> example
      return hostParts[0];
    } catch {
      return undefined;
    }
  }

  /**
   * Build a server config from parsed flags
   */
  protected static buildConfig(flags: ParsedCliFlags): McpServerConfig {
    const config: McpServerConfig = {};

    // Determine transport type
    if (flags.transport) {
      config.type = flags.transport;
    } else if (flags.command) {
      config.type = 'stdio';
    } else if (flags.url) {
      config.type = flags.url.includes('/sse') ? 'sse' : 'http';
    }

    // Set URL or command/args
    if (flags.url) {
      config.url = flags.url;
    }
    if (flags.command) {
      config.command = flags.command;
    }
    if (flags.args.length > 0) {
      config.args = flags.args;
    }

    // Set environment variables
    if (Object.keys(flags.env).length > 0) {
      config.env = flags.env;
    }

    // Set headers
    if (Object.keys(flags.headers).length > 0) {
      config.headers = flags.headers;
    }

    // Set optional fields
    if (flags.timeout !== undefined) {
      config.timeout = flags.timeout;
    }
    if (flags.includeTools && flags.includeTools.length > 0) {
      config.includeTools = flags.includeTools;
    }
    if (flags.excludeTools && flags.excludeTools.length > 0) {
      config.excludeTools = flags.excludeTools;
    }

    return config;
  }

  /**
   * Determine server name from flags
   */
  protected static determineName(flags: ParsedCliFlags): string {
    // Use explicitly provided name first
    if (flags.name) {
      return flags.name;
    }

    // Try to extract from URL
    if (flags.url) {
      const nameFromUrl = BaseCliParser.extractNameFromUrl(flags.url);
      if (nameFromUrl) {
        return nameFromUrl;
      }
    }

    // Try to extract from args
    if (flags.args.length > 0) {
      const nameFromArgs = BaseCliParser.extractNameFromArgs(flags.args);
      if (nameFromArgs) {
        return nameFromArgs;
      }
    }

    // Try to extract from command
    if (flags.command) {
      const nameFromCommand = BaseCliParser.extractNameFromArgs([flags.command]);
      if (nameFromCommand) {
        return nameFromCommand;
      }
    }

    // Fallback to generic name
    return 'mcp-server';
  }

  /**
   * Build the final parse result
   */
  protected buildResult(flags: ParsedCliFlags, warnings: string[] = []): CliParseResult {
    const name = BaseCliParser.determineName(flags);
    const config = BaseCliParser.buildConfig(flags);

    // Validate minimum requirements
    if (!config.command && !config.url) {
      return {
        success: false,
        servers: [],
        error: 'Configuration must have either a command (for STDIO) or URL (for HTTP)',
        tool: this.tool,
      };
    }

    // Add warnings for unknown flags
    if (flags.unknownFlags.length > 0) {
      warnings.push(`Unknown flags ignored: ${flags.unknownFlags.join(', ')}`);
    }

    const server: ParsedMcpServer = { name, config };

    return {
      success: true,
      servers: [server],
      warnings: warnings.length > 0 ? warnings : undefined,
      tool: this.tool,
    };
  }

  /**
   * Helper to create a failure result
   */
  protected failResult(error: string): CliParseResult {
    return {
      success: false,
      servers: [],
      error,
      tool: this.tool,
    };
  }

  /**
   * Check if a string looks like a URL
   */
  protected static isUrl(str: string): boolean {
    return str.startsWith('http://') || str.startsWith('https://');
  }

  /**
   * Check if a string looks like a flag
   */
  protected static isFlag(str: string): boolean {
    return str.startsWith('-');
  }
}
